import { get } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import { useTheme } from 'styled-components';
import { getIntlKey, useStrategiesContext } from '../../contexts';
import { useDynamicCallback, useIntl, useOrderIncrements } from '../../hooks';
import { parseColor } from '../../styles';
import { SideEnum, type CustomerOrder, type Order, type Security } from '../../types';
import { getPricingReference, isKeyIn, isOrderComplete, resolvePricingModeDisplaySpec } from '../../utils';
import { InlineFormattedNumber } from '../FormattedNumber';
import { Icon, IconName } from '../Icons';
import { PriceInlineFormatted } from '../SmartSummary';
import { Text } from '../Text';
import { MinTd, OrderSide, Summary, Table, Tbody, Td } from './styles';

interface OrderCardSummaryProps extends Partial<
  Pick<
    Order,
    | 'PricingReference'
    | 'PricingMode'
    | 'Allocation'
    | 'OrderQty'
    | 'Currency'
    | 'OrdStatus'
    | 'Price'
    | 'CumQty'
    | 'AvgPxAllIn'
    | 'AvgPx'
    | 'Strategy'
    | 'Parameters'
  >
> {
  OrdStatus: Order['OrdStatus'];
  Side: Order['Side'];
  security: Security;
  showFees: boolean;
  customerOrder?: CustomerOrder;
  isCustomerOrderCard?: boolean;
  showGroup?: boolean;

  tableDetails?: { id: string; label: ReactNode; value: ReactNode }[];

  subAccountDetails?: ReactNode;
}

const messages = defineMessages({
  filled: {
    id: 'OrderCard.filled',
    defaultMessage: 'Filled',
  },
  customerFilled: {
    id: 'OrderCard.customerFilled',
    defaultMessage: 'Customer Filled',
  },
  buy: {
    defaultMessage: 'Buy',
    id: 'OrderCard.buy',
  },
  sell: {
    defaultMessage: 'Sell',
    id: 'OrderCard.sell',
  },
  customer: {
    defaultMessage: 'Customer',
    id: 'OrderCard.customer',
  },
});

export const OrderCardSummary = (props: OrderCardSummaryProps) => {
  const {
    Side,
    OrderQty,
    Currency = '',
    OrdStatus,
    Price,
    CumQty,
    AvgPxAllIn,
    AvgPx,
    Strategy,
    Parameters,
    security,
    showFees,
    customerOrder,
    isCustomerOrderCard,
    subAccountDetails,
    tableDetails,
  } = props;

  const orderIncrements = useOrderIncrements({
    Currency,
    Symbol: security.Symbol,
  });

  const { priceCurrency, priceIncrement, priceDisplaySpec, toDisplayPrice } = resolvePricingModeDisplaySpec({
    pricingMode: props.PricingMode,
    security,
  });

  const theme = useTheme();
  const inlineFormattedNumberBg = parseColor(theme, isOrderComplete(OrdStatus) ? 'gray.010' : 'gray.main');
  const { formatMessage } = useIntl();

  const renderedTableDetails = useMemo(() => {
    return tableDetails?.map(detail => (
      <tr key={detail.id}>
        <Td>{detail.label}</Td>
        <MinTd />
        <MinTd />
        <MinTd style={{ color: theme.colorTextImportant }}>{detail.value}</MinTd>
      </tr>
    ));
  }, [tableDetails, theme.colorTextImportant]);

  const { strategiesByName } = useStrategiesContext();

  const endPriceToDisplay = useMemo<string | undefined>(() => {
    if (Strategy && strategiesByName.has(Strategy)) {
      const strat = strategiesByName.get(Strategy);
      if (strat?.showPriceAsRange && strat.endPriceParamName) {
        return get(Parameters, strat.endPriceParamName);
      }
    }
    return undefined;
  }, [Strategy, strategiesByName, Parameters]);

  const getOrderSummaryRow = useDynamicCallback((isCustomerRow: boolean) => {
    const useCustomerLabels = isCustomerRow || isCustomerOrderCard;
    const priceToDisplay = !isCustomerRow ? Price : customerOrder?.Price;
    const sideIntlKey = getIntlKey(Side);
    const translatedSide = !Side ? null : isKeyIn(sideIntlKey, messages) ? formatMessage(messages[sideIntlKey]) : Side;
    return (
      <>
        <tr>
          <Td>
            <OrderSide isBuy={Side === SideEnum.Buy} isSell={Side === SideEnum.Sell}>
              {(useCustomerLabels ? `${formatMessage(messages.customer)} ` : '') + translatedSide}
            </OrderSide>
          </Td>
          <MinTd>
            <InlineFormattedNumber
              number={!isCustomerRow ? OrderQty : customerOrder?.OrderQty}
              increment={orderIncrements.quantityIncrement}
              specification={priceDisplaySpec}
              currency={Currency}
              background={inlineFormattedNumberBg}
            />
          </MinTd>
          <MinTd>
            <Text color="colorTextSubtle">
              <Icon icon={IconName.AtSymbol} />
            </Text>
          </MinTd>
          <MinTd>
            {priceToDisplay ? (
              <PriceInlineFormatted
                showPriceReference
                entity={{
                  PricingMode: props.PricingMode,
                  PricingReference: props.PricingReference,
                }}
                price={priceToDisplay}
                endPrice={endPriceToDisplay}
                security={security}
                priceField="Price"
              />
            ) : (
              <InlineFormattedNumber
                number={
                  getPricingReference({ PricingMode: props.PricingMode, PricingReference: props.PricingReference })
                    ?.value
                }
                currency={
                  getPricingReference({ PricingMode: props.PricingMode, PricingReference: props.PricingReference })
                    ?.currency
                }
                align="right"
              />
            )}
          </MinTd>
        </tr>
        <tr>
          <Td>{useCustomerLabels ? formatMessage(messages.customerFilled) : formatMessage(messages.filled)}</Td>
          <MinTd data-testid="order-card-summary-filled">
            <InlineFormattedNumber
              number={!isCustomerRow ? CumQty : customerOrder?.CumQty}
              increment={orderIncrements.quantityIncrement}
              specification={priceDisplaySpec}
              currency={Currency}
              background={inlineFormattedNumberBg}
            />
          </MinTd>
          <MinTd>
            <Text color="colorTextSubtle">
              <Icon icon={IconName.AtSymbol} />
            </Text>
          </MinTd>
          <MinTd data-testid="order-card-summary-filled-price">
            <InlineFormattedNumber
              number={toDisplayPrice(!isCustomerRow ? (showFees ? AvgPxAllIn || AvgPx : AvgPx) : customerOrder?.AvgPx)}
              increment={priceIncrement}
              specification={priceDisplaySpec}
              currency={priceCurrency}
              background={inlineFormattedNumberBg}
              startIcon={showFees ? IconName.Fees : undefined}
              align="right"
            />
          </MinTd>
        </tr>
      </>
    );
  });

  return (
    <Summary>
      <Table>
        <Tbody>
          {customerOrder && getOrderSummaryRow(true)}
          {getOrderSummaryRow(false)}
          {renderedTableDetails}
        </Tbody>
      </Table>
      <>{subAccountDetails}</>
    </Summary>
  );
};
